import { useLanguage } from "../context/LanguageContext";
import useSiteText from "../hooks/useSiteText";
import OptimizedImage from "../components/OptimizedImage";
import { Award, Users, Shield, Zap } from "lucide-react";
import AnimatedCounter from "../components/AnimatedCounter";
import FadeIn from "../components/FadeIn";

const AboutPage = () => {
  const { t } = useLanguage();
  const text = useSiteText();

  const valueIcons = [Award, Users, Shield, Zap];

  return (
    <div className="pt-20" data-testid="about-page">
      {/* Header */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.nav.about}</p>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {text("about.title", t.about.title)}
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl">{text("about.subtitle", t.about.subtitle)}</p>
        </div>
      </section>

      {/* History */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {text("about.history.title", t.about.history.title)}
              </h2>
              <p className="text-zinc-600 text-lg leading-relaxed">{text("about.history.content", t.about.history.content)}</p>
            </div>
            <div className="relative h-[400px] lg:h-[500px] overflow-hidden">
              <OptimizedImage
                src={text("about.history.image", "https://customer-assets.emergentagent.com/job_meat-chain-pro/artifacts/1rw2mzuu_R6II1434.JPG")}
                alt="Tamari facility"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy — text + full photo side by side */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="grid lg:grid-cols-2">
          <div className="flex items-center px-6 sm:px-10 lg:px-16 py-20 lg:py-32">
            <div className="max-w-xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-6">{text("about.philosophy.title", t.about.philosophy.title)}</p>
              <p className="text-2xl sm:text-3xl text-white leading-relaxed font-light" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {text("about.philosophy.content", t.about.philosophy.content)}
              </p>
            </div>
          </div>
          <div className="bg-[#0A0A0A] flex items-center justify-center">
            <OptimizedImage
              src={text("about.philosophy.image", "https://customer-assets.emergentagent.com/job_meat-chain-pro/artifacts/dtp0kaax_R6II1460.JPG")}
              alt="Tamari philosophy"
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="w-full h-full max-h-[700px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold tracking-tight mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {text("about.mission.title", t.about.mission.title)}
            </h2>
            <p className="text-xl text-zinc-300 leading-relaxed">{text("about.mission.content", t.about.mission.content)}</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold tracking-tight text-[#0A0A0A] mb-12" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.about.values.title}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200">
            {t.about.values.items.map((value, index) => {
              const Icon = valueIcons[index];
              return (
                <FadeIn key={index} delay={index * 120} className="bg-white p-8">
                  <Icon className="w-10 h-10 text-[#0A0A0A] mb-4" />
                  <h3 className="text-xl font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {value.title}
                  </h3>
                  <p className="text-zinc-600">{value.desc}</p>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold tracking-tight text-[#0A0A0A] mb-12" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.about.numbers.title}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.about.numbers.items.map((item, index) => (
              <FadeIn key={index} delay={index * 120} className="text-center" data-testid="about-number-card">
                <AnimatedCounter
                  value={item.number}
                  className="block text-6xl font-black tracking-tighter text-[#0A0A0A] mb-2"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                />
                <div className="text-zinc-600">{item.label}</div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
